// ============================================================
// LoginPage.jsx — Exact React conversion of glass.html
// All styles, animations, and JS logic preserved
// ============================================================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const API = 'http://127.0.0.1:8000';

// notify function — exact mirror of original glass.html notify()
function notify(title, text, icon) {
  Swal.fire({
    title,
    text,
    icon,
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    backdrop: `rgba(0,0,123,0.1) blur(12px)`,
    customClass: { popup: 'glass-popup' },
  });
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login'); // 'login' | 'signup'

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');

  // ─── LOGIN LOGIC ──────────────────────────────────────────
  // Exact mirror of original login() function in glass.html
  async function login(e) {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      notify('Missing Fields', 'Please fill in all details.', 'warning');
      return;
    }
    try {
      const res = await fetch(API + '/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (data.message === 'Login success') {
        localStorage.setItem('user', loginEmail);
        localStorage.setItem('role', data.role);
        notify('Access Granted', 'Redirecting to workspace...', 'success');
        setTimeout(() => {
          if (data.role === 'admin') {
            navigate('/dashboard');
          } else {
            navigate('/feedback');
          }
        }, 2000);
      } else {
        notify('Login Failed', data.message, 'error');
      }
    } catch (error) {
      console.error('Login Error:', error);
      notify('Server Error', 'Connection failed. Check backend.', 'error');
    }
  }

  // ─── SIGNUP LOGIC ─────────────────────────────────────────
  // Exact mirror of original signup() function in glass.html
  async function signup(e) {
    e.preventDefault();
    if (!signupName || !signupEmail || !signupPassword) {
      notify('Input Error', 'All fields are mandatory.', 'warning');
      return;
    }
    try {
      const res = await fetch(API + '/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: signupName, email: signupEmail, password: signupPassword }),
      });
      const data = await res.json();
      if (data.message === 'Signup success') {
        notify('Account Created', 'Welcome! Redirecting to login...', 'success');
        setTimeout(() => setActiveTab('login'), 2200);
      } else {
        notify('Signup Failed', data.message, 'error');
      }
    } catch (error) {
      console.error('Signup Error:', error);
      notify('Server Error', 'Registration failed.', 'error');
    }
  }

  return (
    // ─── glass.html body structure ────────────────────────────
    <main className="relative min-h-screen flex flex-col lg:flex-row h-full font-manrope" style={{ color: '#374151' }}>

      {/* ── BEGIN: Background Elements ── exact from glass.html ── */}
      <div
        className="fixed inset-0 -z-10 overflow-hidden pointer-events-none"
        data-purpose="background-visuals"
      >
        {/* Radial Glows */}
        <div className="absolute top-[-20%] left-[-20%] w-[800px] h-[800px] bg-glow rounded-full opacity-40"></div>
        <div className="absolute bottom-[-20%] right-[-20%] w-[900px] h-[900px] bg-glow rounded-full opacity-30"></div>
        {/* 3D Floating Elements */}
        <div
          className="absolute top-1/4 left-1/4 w-32 h-32 sphere animate-float opacity-60 blur-sm"
          style={{ animationDuration: '12s' }}
        ></div>
        <div
          className="absolute bottom-1/3 left-1/2 w-48 h-48 cube rounded-3xl animate-pulse-slow opacity-40 blur-md"
          style={{ animationDuration: '15s' }}
        ></div>
        <div
          className="absolute top-1/2 right-1/4 w-40 h-40 sphere animate-float opacity-50 blur-none"
          style={{ animationDelay: '-2s', animationDuration: '10s' }}
        ></div>
        <div
          className="absolute top-20 right-20 w-24 h-24 cube rounded-2xl animate-float opacity-30 blur-sm"
          style={{ animationDelay: '-5s' }}
        ></div>
        {/* Organic Blobs */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-white/20 organic-blob animate-float blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-slate-100/30 organic-blob animate-pulse-slow blur-2xl"></div>
      </div>
      {/* ── END: Background Elements ── */}

      {/* ── BEGIN: Branding Side (Desktop Only) — exact from glass.html ── */}
      <section
        className="flex w-full lg:w-1/2 relative flex-col justify-center items-center px-6 lg:px-12 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)',
          borderRight: '1px solid rgba(22, 163, 74, 0.15)',
        }}
        data-purpose="branding-visuals"
      >
        {/* Grid Motion Container */}
        <div className="absolute inset-0 w-full h-[200%] animate-grid-move flex flex-wrap gap-4 p-8 pointer-events-none opacity-20">
          <div className="frosted-card w-32 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.35)' }}></div>
          <div className="frosted-card w-48 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.25)' }}></div>
          <div className="frosted-card w-32 h-48 rounded-3xl" style={{ background: 'rgba(255,255,255,0.15)' }}></div>
          <div className="frosted-card w-40 h-40 rounded-3xl" style={{ background: 'rgba(255,255,255,0.3)' }}></div>
          <div className="frosted-card w-32 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.35)' }}></div>
          <div className="frosted-card w-56 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.15)' }}></div>
          <div className="frosted-card w-32 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.35)' }}></div>
          <div className="frosted-card w-32 h-40 rounded-3xl" style={{ background: 'rgba(255,255,255,0.25)' }}></div>
          <div className="frosted-card w-40 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.35)' }}></div>
          <div className="frosted-card w-32 h-32 rounded-3xl" style={{ background: 'rgba(255,255,255,0.3)' }}></div>
        </div>
        <div className="relative z-10 text-center">
          <h1 className="text-6xl font-bold font-manrope tracking-tight mb-4 text-[#0f291b]">
            Corporate<span className="text-[#15803d]">Feedback</span>
          </h1>
          <p className="text-lg max-w-md mx-auto text-[#052e16] font-semibold opacity-90 leading-relaxed">
            Experience data analytics with the clarity of cloud and the precision of slate.
          </p>
        </div>
      </section>
      {/* ── END: Branding Side ── */}

      {/* ── BEGIN: Auth Side — exact from glass.html ── */}
      <section
        className="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-24 relative py-20 lg:py-0"
        style={{ background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)' }}
        data-purpose="auth-container"
      >
        {/* Mobile Logo */}
        <div className="absolute top-8 left-0 right-0 text-center lg:hidden w-full">
          <h1 className="text-3xl font-bold font-manrope tracking-tight text-dark_slate">
            Corporate<span className="text-slate_accent">Feedback</span>
          </h1>
        </div>

        <div className="w-full max-w-md glass-panel rounded-[2.5rem] p-8 sm:p-12 shadow-2xl backdrop-blur-[50px] saturate-[180%] bg-white/20 relative">
          {/* Decorative image (original glass.html had this) */}
          <img
            src="/like1-removebg-preview.png"
            className="absolute top-1/2 right-2/3 w-[950px] opacity-40 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
            alt=""
            onError={(e) => { e.target.style.display = 'none'; }}
          />

          {/* ── BEGIN: Form Toggle ── */}
          <div className="flex items-center gap-8 mb-10" data-purpose="form-toggles">
            <button
              className={`text-2xl font-manrope font-bold pb-2 transition-all ${
                activeTab === 'login'
                  ? 'text-dark_slate border-b-2 border-dark_slate'
                  : 'text-slate-400 hover:text-dark_slate/60'
              }`}
              onClick={() => setActiveTab('login')}
            >
              Login
            </button>
            <button
              className={`text-2xl font-manrope font-bold pb-2 transition-all ${
                activeTab === 'signup'
                  ? 'text-dark_slate border-b-2 border-dark_slate'
                  : 'text-slate-400 hover:text-dark_slate/60'
              }`}
              onClick={() => setActiveTab('signup')}
            >
              Sign Up
            </button>
          </div>
          {/* ── END: Form Toggle ── */}

          {/* ── BEGIN: Login Form — exact from glass.html ── */}
          {activeTab === 'login' && (
            <form className="space-y-6" onSubmit={login}>
              <div>
                <label className="block text-sm font-semibold text-dark_slate mb-2">
                  Email Address
                </label>
                <input
                  id="loginEmail"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-5 py-4 rounded-2xl bg-white/60 border-slate-200 border focus:border-dark_slate focus:ring-0 transition-all placeholder:text-slate-400"
                  placeholder="varun@gmail.com"
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <label className="block text-sm font-semibold text-dark_slate">
                    Password
                  </label>
                </div>
                <input
                  id="loginPassword"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-5 py-4 rounded-2xl bg-white/60 border-slate-200 border focus:border-dark_slate focus:ring-0 transition-all placeholder:text-slate-400"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                className="w-full btn-primary-grey text-white py-4 rounded-2xl font-bold shadow-lg transition-all duration-300"
              >
                Sign In
              </button>
            </form>
          )}
          {/* ── END: Login Form ── */}

          {/* ── BEGIN: Sign-up Form — exact from glass.html ── */}
          {activeTab === 'signup' && (
            <form className="space-y-5" onSubmit={signup}>
              <div>
                <label className="block text-sm font-semibold text-dark_slate mb-2">
                  Full Name
                </label>
                <input
                  id="signupName"
                  type="text"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  className="w-full px-5 py-4 rounded-2xl bg-white/60 border-slate-200 border"
                  placeholder="Varun"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-dark_slate mb-2">
                  Email Address
                </label>
                <input
                  id="signupEmail"
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="w-full px-5 py-4 rounded-2xl bg-white/60 border-slate-200 border"
                  placeholder="varun@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-dark_slate mb-2">
                  Create Password
                </label>
                <input
                  id="signupPassword"
                  type="password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  className="w-full px-5 py-4 rounded-2xl bg-white/60 border-slate-200 border"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                className="w-full btn-primary-grey text-white py-4 rounded-2xl font-bold shadow-lg"
              >
                Create Account
              </button>
            </form>
          )}
          {/* ── END: Sign-up Form ── */}

          {/* ── BEGIN: Social Auth — exact from glass.html ── */}
          <div className="mt-10" data-purpose="social-auth">
            <div className="relative flex items-center justify-center mb-8">
              <div className="border-t border-slate-200 w-full absolute"></div>
              <span className="relative bg-transparent px-4 text-xs font-semibold text-slate-400 uppercase tracking-widest">
                Or continue with
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button className="flex items-center justify-center gap-2 py-3 rounded-xl btn-frosted text-dark_slate">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19,4.72C19,4.72 16.56,2 12.1,2C6.42,2 2.03,6.8 2.03,12C2.03,17.05 6.16,22 12.25,22C17.6,22 21.5,18.33 21.5,12.91C21.5,11.76 21.35,11.1 21.35,11.1V11.1Z"
                    fill="currentColor"
                  ></path>
                </svg>
                <span className="text-sm font-bold">Google</span>
              </button>
              <button className="flex items-center justify-center gap-2 py-3 rounded-xl btn-frosted text-dark_slate">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    d="M12,2A10,10 0 0,0 2,12C2,16.42 4.87,20.17 8.84,21.5C9.34,21.58 9.5,21.27 9.5,21.03C9.5,20.81 9.5,20.14 9.5,19.4C6.73,20 6.14,18.07 6.14,18.07C5.68,16.92 5.03,16.61 5.03,16.61C4.12,16 5.1,16 5.1,16C6.1,16.07 6.63,17.03 6.63,17.03C7.5,18.54 8.95,18.11 9.5,17.88C9.6,17.25 9.85,16.82 10.12,16.59C7.91,16.34 5.59,15.47 5.59,11.66C5.59,10.57 5.98,9.68 6.62,8.97C6.52,8.72 6.17,7.7 6.72,6.33C6.72,6.33 7.56,6.06 9.47,7.35C10.27,7.13 11.13,7.02 12,7C12.87,7.02 13.73,7.13 14.53,7.35C16.44,6.06 17.28,6.33 17.28,6.33C17.83,7.7 17.48,8.72 17.38,8.97C18.03,9.68 18.41,10.57 18.41,11.66C18.41,15.48 16.08,16.34 13.87,16.58C14.23,16.89 14.55,17.5 14.55,18.45C14.55,19.81 14.54,20.9 14.54,21.23C14.54,21.5 14.71,21.82 15.22,21.72C19.17,20.37 22,16.58 22,12A10,10 0 0,0 12,2Z"
                    fill="currentColor"
                  ></path>
                </svg>
                <span className="text-sm font-bold">GitHub</span>
              </button>
            </div>
          </div>
          {/* ── END: Social Auth ── */}
        </div>
      </section>
      {/* ── END: Auth Side ── */}
    </main>
  );
}
